import type { RulePropertyEnum } from '~/store/services/RuleSetServiceV2/types';
import type { FormInstance } from 'antd/es/form/Form';
import type { RulePageForm } from './types';
import { CriteriaSelection, HazmatCriteriaOptions, RULE_ACTION_TYPE_TO_CRITERIA_SELECTION } from './types';
import type {
  PartnerRule,
  PartnerRuleCriterion,
  PartnerRuleCriterionDefinition,
  PartnerRuleUpdate,
} from '@shipium-corp/shipium-ts-types';

export const toggleRuleStatus = (rule: PartnerRule, isActive: boolean): PartnerRule => {
  return {
    ...rule,
    active: isActive,
  };
};

export const buildUpdateRulePayload = (rule: PartnerRule): PartnerRuleUpdate => {
  return {
    active: rule.active!,
    archived: rule.archived!,
    carrierAndServiceMethodList: rule.carrierAndServiceMethodList,
    listType: rule.listType,
    name: rule.name,
    description: rule.description,
    tenantIds: rule.tenantIds,
    terminating: rule.terminating,
    criteria: rule.criteria.map<PartnerRuleCriterionDefinition>((criteria) => {
      return {
        compareValue: criteria.compareValue!,
        ruleComparatorEnum: criteria.ruleComparator.ruleComparatorEnum!,
        rulePropertyEnum: criteria.ruleProperty.rulePropertyEnum!,
        scalarUnitEnum: criteria?.scalarUnit?.scalarUnitEnum,
      };
    }),
  };
};

export const EMPTY_RULE_PAGE_FORM: RulePageForm = {
  name: '',
  description: '',
  tenantCriteria: CriteriaSelection.DISABLED,
  tenants: [],
  originCriteria: CriteriaSelection.DISABLED,
  origins: [],
  shipFromCriteria: CriteriaSelection.DISABLED,
  useShipFromCountry: false,
  originCountry: [],
  originCountryCriteria: CriteriaSelection.DISABLED,
  useShipFromRegion: false,
  originRegion: [],
  originRegionCriteria: CriteriaSelection.DISABLED,
  useShipFromPostalCodes: false,
  originPostalCodes: [],
  originPostalCriteria: CriteriaSelection.DISABLED,
  destinationCriteria: CriteriaSelection.DISABLED,
  useCountry: false,
  countryCriteria: CriteriaSelection.DISABLED,
  country: [],
  useRegion: false,
  regionCriteria: CriteriaSelection.DISABLED,
  region: [],
  usePostalCodes: false,
  postalCodeCriteria: CriteriaSelection.DISABLED,
  postalCodes: [],
  packageCriteria: CriteriaSelection.DISABLED,
  packageWeightComparator: '',
  packageWeightUnitValue: '',
  packageValue: '',
  poBoxCriteria: CriteriaSelection.DISABLED,
  includeHcAndRr: false,
  shipmentTagCriteria: CriteriaSelection.DISABLED,
  shipmentTags: [],
  carrierSelectionCriteria: CriteriaSelection.INCLUDE_ONLY,
  eligibleCarrierServiceMethods: [],
  exactDateDeliveryCriteria: CriteriaSelection.DISABLED,
  shipOptionCriteria: CriteriaSelection.DISABLED,
  shipOptions: [],
  hazardousCriteria: CriteriaSelection.DISABLED,
  hazardousCriteriaOptions: HazmatCriteriaOptions.NONE,
};

export const initializeEmptyForm = (form: FormInstance<Partial<RulePageForm>>): void => {
  form.setFieldsValue(EMPTY_RULE_PAGE_FORM);
};

export function isCriteriaDisabled(value: CriteriaSelection | undefined) {
  return value === CriteriaSelection.DISABLED || !value;
}

export function findCriteria(criteria: PartnerRuleCriterion[], rulePropertyEnum: RulePropertyEnum) {
  return criteria.find((ruleInfo) => {
    return ruleInfo.ruleProperty.rulePropertyEnum === rulePropertyEnum;
  });
}

export const initializePopulatedForm = (form: FormInstance<Partial<RulePageForm>>, rule: PartnerRule): void => {
  const { criteria, listType, carrierAndServiceMethodList } = rule;

  const existingOriginRule = findCriteria(criteria, 'origin');
  const existingOriginCountryRule = findCriteria(criteria, 'origin_country');
  const existingOriginRegionRule = findCriteria(criteria, 'origin_region');
  const existingOriginPostalRule = findCriteria(criteria, 'origin_postal');
  const existingWeightRule = findCriteria(criteria, 'weight');
  const existingPoBoxRule = findCriteria(criteria, 'po_box');
  const existingPoBoxPlusHcAndRrRule = findCriteria(criteria, 'po_box_plus_hc_and_rr' as RulePropertyEnum);
  const existingShipmentTagRule = findCriteria(criteria, 'shipment_tag');
  const existingDestinationCountryRule = findCriteria(criteria, 'destination_country');
  const existingDestinationRegionRule = findCriteria(criteria, 'destination_region');
  const existingDestinationPostalRule = findCriteria(criteria, 'destination_postal');
  const existingExactDateRule = findCriteria(criteria, 'exact_date_delivery');
  const existingShipOptionRule = findCriteria(criteria, 'ship_option');

  const formValues: RulePageForm = {
    name: rule.name!,
    description: rule.description!,
    tenantCriteria: (() => {
      if (rule.tenantIds?.length) {
        return CriteriaSelection.SPECIFIC;
      }

      return CriteriaSelection.DISABLED;
    })(),
    tenants: rule.tenantIds ?? [],
    originCriteria: (() => {
      if (existingOriginRule?.ruleComparator?.ruleComparatorEnum === 'in') {
        return CriteriaSelection.INCLUDE_ONLY;
      } else if (existingOriginRule?.ruleComparator?.ruleComparatorEnum === 'not_in') {
        return CriteriaSelection.OMIT_ONLY;
      }

      return CriteriaSelection.DISABLED;
    })(),
    origins:
      existingOriginRule && Array.isArray(existingOriginRule.compareValue) ? existingOriginRule.compareValue : [],
    destinationCriteria: (() => {
      if (existingDestinationCountryRule || existingDestinationPostalRule) {
        return CriteriaSelection.SPECIFIC;
      }

      return CriteriaSelection.DISABLED;
    })(),
    useCountry: existingDestinationCountryRule ? true : false,
    countryCriteria: (() => {
      if (existingDestinationCountryRule?.ruleComparator?.ruleComparatorEnum === 'in') {
        return CriteriaSelection.INCLUDE_ONLY;
      } else if (existingDestinationCountryRule?.ruleComparator?.ruleComparatorEnum === 'not_in') {
        return CriteriaSelection.OMIT_ONLY;
      }

      return CriteriaSelection.DISABLED;
    })(),
    country:
      existingDestinationCountryRule && Array.isArray(existingDestinationCountryRule.compareValue)
        ? existingDestinationCountryRule.compareValue
        : [],
    useRegion: existingDestinationRegionRule ? true : false,
    regionCriteria: (() => {
      if (existingDestinationRegionRule?.ruleComparator?.ruleComparatorEnum === 'in') {
        return CriteriaSelection.INCLUDE_ONLY;
      } else if (existingDestinationRegionRule?.ruleComparator?.ruleComparatorEnum === 'not_in') {
        return CriteriaSelection.OMIT_ONLY;
      }

      return CriteriaSelection.DISABLED;
    })(),
    region:
      existingDestinationRegionRule && Array.isArray(existingDestinationRegionRule.compareValue)
        ? existingDestinationRegionRule.compareValue
        : [],
    usePostalCodes: existingDestinationPostalRule ? true : false,
    postalCodeCriteria: (() => {
      if (existingDestinationPostalRule?.ruleComparator?.ruleComparatorEnum === 'in') {
        return CriteriaSelection.INCLUDE_ONLY;
      } else if (existingDestinationPostalRule?.ruleComparator?.ruleComparatorEnum === 'not_in') {
        return CriteriaSelection.OMIT_ONLY;
      }

      return CriteriaSelection.DISABLED;
    })(),
    postalCodes:
      existingDestinationPostalRule && Array.isArray(existingDestinationPostalRule.compareValue)
        ? existingDestinationPostalRule.compareValue
        : [],
    packageCriteria: (() => {
      if (existingWeightRule) {
        return CriteriaSelection.SPECIFIC;
      }

      return CriteriaSelection.DISABLED;
    })(),
    packageWeightUnitValue: existingWeightRule?.scalarUnit?.scalarUnitEnum ?? '',
    packageWeightComparator: existingWeightRule?.ruleComparator?.ruleComparatorEnum ?? '',
    packageValue:
      !existingWeightRule?.compareValue || Array.isArray(existingWeightRule?.compareValue)
        ? ''
        : String(existingWeightRule.compareValue),
    poBoxCriteria: (() => {
      const activePoBoxRule = existingPoBoxRule || existingPoBoxPlusHcAndRrRule;
      if (activePoBoxRule) {
        if (activePoBoxRule?.compareValue) {
          return CriteriaSelection.INCLUDE_ONLY;
        } else {
          return CriteriaSelection.OMIT_ONLY;
        }
      }

      return CriteriaSelection.DISABLED;
    })(),
    includeHcAndRr: !!existingPoBoxPlusHcAndRrRule,
    shipmentTagCriteria: (() => {
      if (existingShipmentTagRule?.ruleComparator?.ruleComparatorEnum === 'in') {
        return CriteriaSelection.INCLUDE_ONLY;
      } else if (existingShipmentTagRule?.ruleComparator?.ruleComparatorEnum === 'not_in') {
        return CriteriaSelection.OMIT_ONLY;
      }

      return CriteriaSelection.DISABLED;
    })(),
    shipmentTags:
      existingShipmentTagRule && Array.isArray(existingShipmentTagRule.compareValue)
        ? existingShipmentTagRule.compareValue
        : [],
    carrierSelectionCriteria: RULE_ACTION_TYPE_TO_CRITERIA_SELECTION[listType],
    eligibleCarrierServiceMethods: [...carrierAndServiceMethodList],
    exactDateDeliveryCriteria: (() => {
      if (existingExactDateRule?.compareValue) {
        return CriteriaSelection.INCLUDE_ONLY;
      } else if (existingExactDateRule) {
        return CriteriaSelection.OMIT_ONLY;
      }

      return CriteriaSelection.DISABLED;
    })(),
    shipOptionCriteria: (() => {
      if (existingShipOptionRule?.ruleComparator?.ruleComparatorEnum === 'in') {
        return CriteriaSelection.INCLUDE_ONLY;
      } else if (existingShipOptionRule?.ruleComparator?.ruleComparatorEnum === 'not_in') {
        return CriteriaSelection.OMIT_ONLY;
      }

      return CriteriaSelection.DISABLED;
    })(),
    shipOptions:
      existingShipOptionRule && Array.isArray(existingShipOptionRule.compareValue)
        ? existingShipOptionRule.compareValue
        : [],
    shipFromCriteria: (() => {
      if (existingOriginCountryRule || existingOriginPostalRule || existingOriginRegionRule) {
        return CriteriaSelection.SPECIFIC;
      }

      return CriteriaSelection.DISABLED;
    })(),
    useShipFromCountry: existingOriginCountryRule ? true : false,
    originCountryCriteria: (() => {
      if (existingOriginCountryRule?.ruleComparator?.ruleComparatorEnum === 'in') {
        return CriteriaSelection.INCLUDE_ONLY;
      } else if (existingOriginCountryRule?.ruleComparator?.ruleComparatorEnum === 'not_in') {
        return CriteriaSelection.OMIT_ONLY;
      }

      return CriteriaSelection.DISABLED;
    })(),
    originCountry:
      existingOriginCountryRule && Array.isArray(existingOriginCountryRule.compareValue)
        ? existingOriginCountryRule.compareValue
        : [],
    useShipFromRegion: existingOriginRegionRule ? true : false,
    originRegionCriteria: (() => {
      if (existingOriginRegionRule?.ruleComparator?.ruleComparatorEnum === 'in') {
        return CriteriaSelection.INCLUDE_ONLY;
      } else if (existingOriginRegionRule?.ruleComparator?.ruleComparatorEnum === 'not_in') {
        return CriteriaSelection.OMIT_ONLY;
      }

      return CriteriaSelection.DISABLED;
    })(),
    originRegion:
      existingOriginRegionRule && Array.isArray(existingOriginRegionRule.compareValue)
        ? existingOriginRegionRule.compareValue
        : [],
    useShipFromPostalCodes: existingOriginPostalRule ? true : false,
    originPostalCriteria: (() => {
      if (existingOriginPostalRule?.ruleComparator?.ruleComparatorEnum === 'in') {
        return CriteriaSelection.INCLUDE_ONLY;
      } else if (existingOriginPostalRule?.ruleComparator?.ruleComparatorEnum === 'not_in') {
        return CriteriaSelection.OMIT_ONLY;
      }

      return CriteriaSelection.DISABLED;
    })(),
    originPostalCodes:
      existingOriginPostalRule && Array.isArray(existingOriginPostalRule.compareValue)
        ? existingOriginPostalRule.compareValue
        : [],
    hazardousCriteria: (() => {
      const limitedQuantityCriteria = findCriteria(criteria, 'limited_quantity');
      const hazmatCriteria = findCriteria(criteria, 'hazmat');
      const fullHazmatCriteria = findCriteria(criteria, 'full_hazmat');

      if (
        limitedQuantityCriteria?.compareValue === (true as any) ||
        hazmatCriteria?.compareValue === (true as any) ||
        fullHazmatCriteria?.compareValue === (true as any)
      ) {
        return CriteriaSelection.INCLUDE_ONLY;
      } else if (
        limitedQuantityCriteria?.compareValue === (false as any) ||
        hazmatCriteria?.compareValue === (false as any) ||
        fullHazmatCriteria?.compareValue === (false as any)
      ) {
        return CriteriaSelection.OMIT_ONLY;
      }

      return CriteriaSelection.DISABLED;
    })(),
    hazardousCriteriaOptions: (() => {
      const limitedQuantityCriteria = findCriteria(criteria, 'limited_quantity');
      const fullHazmatCriteria = findCriteria(criteria, 'full_hazmat');
      const hazmatCriteria = findCriteria(criteria, 'hazmat');

      if (limitedQuantityCriteria) {
        return HazmatCriteriaOptions.LIMITED_QUANTITY;
      } else if (fullHazmatCriteria) {
        return HazmatCriteriaOptions.FULL_HAZMAT;
      } else if (hazmatCriteria) {
        return HazmatCriteriaOptions.HAZMAT;
      }

      return HazmatCriteriaOptions.NONE;
    })(),
  };

  form.setFieldsValue(formValues);
};

export const buildOriginCriteria = (rulePageForm: Partial<RulePageForm>): PartnerRuleCriterionDefinition | null => {
  if (isCriteriaDisabled(rulePageForm.originCriteria) || !rulePageForm.origins) {
    return null;
  }

  const compareValue = Array.isArray(rulePageForm.origins) ? rulePageForm.origins : [rulePageForm.origins];
  const ruleComparatorEnum = rulePageForm.originCriteria === CriteriaSelection.INCLUDE_ONLY ? 'in' : 'not_in';
  return {
    compareValue,
    ruleComparatorEnum,
    rulePropertyEnum: 'origin',
  };
};

export const buildShipFromCountryCriteria = (
  rulePageForm: Partial<RulePageForm>,
): PartnerRuleCriterionDefinition | null => {
  if (isCriteriaDisabled(rulePageForm.shipFromCriteria) || !rulePageForm.originCountry) {
    return null;
  }

  return {
    compareValue: Array.isArray(rulePageForm.originCountry) ? rulePageForm.originCountry : [rulePageForm.originCountry],
    ruleComparatorEnum: rulePageForm.originCountryCriteria === CriteriaSelection.INCLUDE_ONLY ? 'in' : 'not_in',
    rulePropertyEnum: 'origin_country',
  };
};

export const buildShipFromRegionCriteria = (
  rulePageForm: Partial<RulePageForm>,
): PartnerRuleCriterionDefinition | null => {
  if (isCriteriaDisabled(rulePageForm.shipFromCriteria) || !rulePageForm.originRegion) {
    return null;
  }

  return {
    compareValue: rulePageForm.originRegion,
    ruleComparatorEnum: rulePageForm.originRegionCriteria === CriteriaSelection.INCLUDE_ONLY ? 'in' : 'not_in',
    rulePropertyEnum: 'origin_region',
  };
};

export const buildShipFromPostalCriteria = (
  rulePageForm: Partial<RulePageForm>,
): PartnerRuleCriterionDefinition | null => {
  if (isCriteriaDisabled(rulePageForm.shipFromCriteria) || !rulePageForm.originPostalCodes?.length) {
    return null;
  }

  return {
    compareValue: rulePageForm.originPostalCodes,
    ruleComparatorEnum: rulePageForm.originPostalCriteria === CriteriaSelection.INCLUDE_ONLY ? 'in' : 'not_in',
    rulePropertyEnum: 'origin_postal',
  };
};

export const buildDestinationCountryCriteria = (
  rulePageForm: Partial<RulePageForm>,
): PartnerRuleCriterionDefinition | null => {
  if (isCriteriaDisabled(rulePageForm.destinationCriteria) || !rulePageForm.useCountry || !rulePageForm.country) {
    return null;
  }

  return {
    compareValue: Array.isArray(rulePageForm.country) ? rulePageForm.country : [rulePageForm.country],
    ruleComparatorEnum: rulePageForm.countryCriteria === CriteriaSelection.INCLUDE_ONLY ? 'in' : 'not_in',
    rulePropertyEnum: 'destination_country',
  };
};

export const buildDestinationRegionCriteria = (
  rulePageForm: Partial<RulePageForm>,
): PartnerRuleCriterionDefinition | null => {
  if (isCriteriaDisabled(rulePageForm.destinationCriteria) || !rulePageForm.useRegion || !rulePageForm.region) {
    return null;
  }

  return {
    compareValue: rulePageForm.region,
    ruleComparatorEnum: rulePageForm.regionCriteria === CriteriaSelection.INCLUDE_ONLY ? 'in' : 'not_in',
    rulePropertyEnum: 'destination_region',
  };
};

export const buildDestinationPostalCriteria = (
  rulePageForm: Partial<RulePageForm>,
): PartnerRuleCriterionDefinition | null => {
  if (isCriteriaDisabled(rulePageForm.destinationCriteria) || !rulePageForm.postalCodes?.length) {
    return null;
  }

  return {
    compareValue: rulePageForm.postalCodes,
    ruleComparatorEnum: rulePageForm.postalCodeCriteria === CriteriaSelection.INCLUDE_ONLY ? 'in' : 'not_in',
    rulePropertyEnum: 'destination_postal',
  };
};

export const buildShipmentTagCriteria = (
  rulePageForm: Partial<RulePageForm>,
): PartnerRuleCriterionDefinition | null => {
  if (isCriteriaDisabled(rulePageForm.shipmentTagCriteria) || !rulePageForm.shipmentTags?.length) {
    return null;
  }

  return {
    compareValue: rulePageForm.shipmentTags,
    ruleComparatorEnum: rulePageForm.shipmentTagCriteria === CriteriaSelection.INCLUDE_ONLY ? 'in' : 'not_in',
    rulePropertyEnum: 'shipment_tag',
  };
};

export const buildPackageWeightCriteria = (
  rulePageForm: Partial<RulePageForm>,
): PartnerRuleCriterionDefinition | null => {
  if (
    isCriteriaDisabled(rulePageForm.packageCriteria) ||
    !rulePageForm.packageWeightComparator ||
    !rulePageForm.packageWeightUnitValue
  ) {
    return null;
  }

  return {
    compareValue: Number(rulePageForm?.packageValue) as any,
    ruleComparatorEnum: rulePageForm.packageWeightComparator,
    rulePropertyEnum: 'weight',
    scalarUnitEnum: rulePageForm.packageWeightUnitValue,
  };
};

export const buildExactDateDeliveryCriteria = (
  rulePageForm: Partial<RulePageForm>,
): PartnerRuleCriterionDefinition | null => {
  if (isCriteriaDisabled(rulePageForm.exactDateDeliveryCriteria) || !rulePageForm.exactDateDeliveryCriteria) {
    return null;
  }

  return {
    compareValue: (rulePageForm.exactDateDeliveryCriteria === CriteriaSelection.INCLUDE_ONLY ? true : false) as any,
    ruleComparatorEnum: 'eq',
    rulePropertyEnum: 'exact_date_delivery',
  };
};

export const buildPoBoxCriteria = (rulePageForm: Partial<RulePageForm>): PartnerRuleCriterionDefinition | null => {
  if (isCriteriaDisabled(rulePageForm.poBoxCriteria)) {
    return null;
  }

  return {
    compareValue: (rulePageForm.poBoxCriteria === CriteriaSelection.INCLUDE_ONLY ? true : false) as any,
    ruleComparatorEnum: 'eq',
    rulePropertyEnum: (rulePageForm.includeHcAndRr ? 'po_box_plus_hc_and_rr' : 'po_box') as RulePropertyEnum,
  };
};

export const buildShipOptionCriteria = (rulePageForm: Partial<RulePageForm>): PartnerRuleCriterionDefinition | null => {
  if (isCriteriaDisabled(rulePageForm.shipOptionCriteria) || !rulePageForm.shipOptions?.length) {
    return null;
  }

  return {
    compareValue: rulePageForm.shipOptions,
    ruleComparatorEnum: rulePageForm.shipOptionCriteria === CriteriaSelection.INCLUDE_ONLY ? 'in' : 'not_in',
    rulePropertyEnum: 'ship_option',
  };
};

export const buildHazardousCriteria = (rulePageForm: Partial<RulePageForm>): PartnerRuleCriterionDefinition | null => {
  if (isCriteriaDisabled(rulePageForm.hazardousCriteria)) {
    return null;
  }

  if (rulePageForm.hazardousCriteriaOptions === HazmatCriteriaOptions.LIMITED_QUANTITY) {
    return {
      compareValue: (rulePageForm.hazardousCriteria === CriteriaSelection.INCLUDE_ONLY ? true : false) as any,
      ruleComparatorEnum: 'eq',
      rulePropertyEnum: 'limited_quantity',
    };
  }

  if (rulePageForm.hazardousCriteriaOptions === HazmatCriteriaOptions.FULL_HAZMAT) {
    return {
      compareValue: (rulePageForm.hazardousCriteria === CriteriaSelection.INCLUDE_ONLY ? true : false) as any,
      ruleComparatorEnum: 'eq',
      rulePropertyEnum: 'full_hazmat',
    };
  }

  if (rulePageForm.hazardousCriteriaOptions === HazmatCriteriaOptions.HAZMAT) {
    return {
      compareValue: (rulePageForm.hazardousCriteria === CriteriaSelection.INCLUDE_ONLY ? true : false) as any,
      ruleComparatorEnum: 'eq',
      rulePropertyEnum: 'hazmat',
    };
  }

  return null;
};
